import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, TextField, Button, IconButton, CircularProgress, Autocomplete, Tooltip } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import CheckIcon from "@mui/icons-material/Check";
import CancelIcon from "@mui/icons-material/Cancel";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { uploadFileWithMediaID, clearUploadMediaIDState } from "../../redux/Slice/Templates/templateSlice";

// A variable's value can be fixed text, or one of these contact fields —
// resolved per-recipient when the broadcast actually sends.
const CONTACT_FIELD_OPTIONS = [
  { label: "Name", token: "$contact.name" },
  { label: "Business Name", token: "$contact.business_name" },
  { label: "Email", token: "$contact.email" },
  { label: "Phone Number", token: "$contact.phone_number" },
];

// Business Name and Email are optional on a contact — unlike Name/Phone Number,
// which are required — so some recipients may get this variable sent blank.
const FIELDS_THAT_MAY_BE_EMPTY = new Set(["$contact.business_name", "$contact.email"]);

// Shared by the header's file input and each carousel card's file input —
// both accept the same three WhatsApp media formats.
const getMediaAcceptInfo = (format) => {
  if (format === "DOCUMENT") return { acceptStr: ".pdf", label: "Allowed: .pdf" };
  if (format === "IMAGE") return { acceptStr: "image/png, image/jpeg", label: "Allowed: .png, .jpg/.jpeg" };
  if (format === "VIDEO") return { acceptStr: "video/mp4", label: "Allowed: .mp4" };
  return { acceptStr: "", label: "" };
};

const VariableValueField = ({ label, value, onChange, placeholder, helperText }) => {
  const matchedOption = CONTACT_FIELD_OPTIONS.find((opt) => opt.token === value);
  const mayBeEmpty = FIELDS_THAT_MAY_BE_EMPTY.has(value);

  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 0.5 }}>
      <Autocomplete
        freeSolo
        size="small"
        options={CONTACT_FIELD_OPTIONS}
        getOptionLabel={(opt) => (typeof opt === "string" ? opt : opt.label)}
        isOptionEqualToValue={(opt, val) => typeof val !== "string" && opt.token === val.token}
        value={matchedOption || value || ""}
        onChange={(_, newValue) => {
          if (newValue && typeof newValue === "object") {
            onChange(newValue.token);
          } else {
            onChange(newValue || "");
          }
        }}
        onInputChange={(_, newInputValue, reason) => {
          if (reason === "input") onChange(newInputValue);
        }}
        renderInput={(params) => (
          <TextField {...params} label={label} placeholder={placeholder} helperText={helperText} />
        )}
        sx={{ width: 240 }}
      />
      {mayBeEmpty && (
        <Tooltip title="This contact field is optional — some recipients may not have it filled in, so the message could show blank for them.">
          <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary", mt: "10px" }} />
        </Tooltip>
      )}
    </Box>
  );
};

// Mirrors the variable-filling fields from Inbox's TemplateSendBox.jsx (header
// media upload, body {{n}} placeholders, dynamic URL button values) — the
// broadcast flow needs the same inputs to actually be able to send a template.
const TemplateVariablesForm = ({ template, initialVariables, onValidityChange, onVariablesChange }) => {
  const dispatch = useDispatch();
  // Aliased from the *MediaID slice — broadcasts need a WhatsApp media id (not a
  // plain server URL) so bulk sends upload the header file once instead of having
  // WhatsApp re-fetch a link per recipient.
  const {
    uploadMediaIDStatus: uploadStatus,
    uploadMediaIDResult: uploadResult,
    uploadMediaIDError: uploadError,
  } = useSelector((state) => state.template);
  const [variables, setVariables] = useState(initialVariables || {});
  const fileInputRef = useRef(null);
  // Which carousel card's file input the current uploadMediaID request belongs to —
  // the redux slice only tracks one upload at a time, same constraint as Inbox's
  // TemplateSendBox.jsx, so uploads for different cards can't run concurrently.
  const [currentCarouselCard, setCurrentCarouselCard] = useState(null);
  const carouselFileInputRefs = useRef({});

  useEffect(() => {
    setVariables(initialVariables || {});
    dispatch(clearUploadMediaIDState());
    setCurrentCarouselCard(null);
    // Only re-run when the template itself changes — initialVariables is a
    // one-time seed, not something that should reset the form on every parent render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, dispatch]);

  useEffect(() => {
    onVariablesChange?.(variables);
  }, [variables, onVariablesChange]);

  const headerComp = template?.components?.find((c) => c.type === "HEADER");
  const headerFormat = headerComp?.format?.toUpperCase() || null;
  const isMediaHeader = headerFormat && ["IMAGE", "VIDEO", "DOCUMENT"].includes(headerFormat);

  // Uploading a header file only puts the media id in Redux (uploadResult) — without this,
  // it never reaches `variables`/onVariablesChange, so the broadcast gets saved and sent
  // with no media at all even though the upload itself succeeded (the bug this fixes).
  // Uses the WhatsApp media-id upload (not a plain server link) so a bulk broadcast
  // uploads the file once and reuses the id, instead of WhatsApp re-fetching a link
  // for every recipient — see sendUploadWithMediaID.service.js.
  // mediaUrl (the persisted public URL sendUploadWithMediaID.service.js also returns
  // alongside the mediaId) rides along here purely so WhatsAppTemplatePreview can
  // render the actual picture — it's a normal saved field, so it also survives
  // reload/logout once the campaign is saved and repopulated via reverseCustomizations.
  // Only reacts to "succeeded" — clearing on "idle" would wipe out headerMedia
  // that was just loaded from initialVariables, since uploadStatus always starts
  // "idle" on mount (the reset effect above dispatches clearUploadMediaIDState()).
  // Explicit clears (cancel button) are handled by handleClearFile instead.
  useEffect(() => {
    if (!isMediaHeader) return;
    if (uploadStatus === "succeeded" && uploadResult) {
      const mediaId = uploadResult.mediaId || "";
      if (!mediaId) return;
      const mediaType = headerFormat.toLowerCase();
      setVariables((prev) => ({
        ...prev,
        headerMedia: { type: mediaType, mediaId, id: mediaId, mediaUrl: uploadResult.mediaUrl || "" },
      }));
    }
  }, [uploadStatus, uploadResult, isMediaHeader, headerFormat]);

  const { acceptStr, label: allowedExtensionsLabel } = getMediaAcceptInfo(headerFormat);

  // Carousel templates carry their own header media per card instead of a
  // top-level HEADER component, so isMediaHeader above stays false for them —
  // no conflict with the upload-media-id effect below reusing the same redux slice.
  const carouselComp = template?.components?.find((c) => c.type?.toLowerCase() === "carousel");
  const carouselCards = useMemo(() => carouselComp?.cards || [], [carouselComp]);
  const getCarouselCardFormat = (card) => {
    const cardHeader = card?.components?.find((c) => (c.type || "").toLowerCase() === "header");
    return (cardHeader?.format || "IMAGE").toUpperCase();
  };

  // Mirrors the header effect above: a carousel card's uploaded file only lands in
  // Redux (uploadResult) — this attaches it to `variables.carousel.cards[cardIndex]`
  // so the broadcast is actually saved/sent with that card's media attached.
  useEffect(() => {
    if (currentCarouselCard === null) return;
    if (uploadStatus === "succeeded" && uploadResult) {
      const mediaId = uploadResult.mediaId || "";
      if (!mediaId) return;
      const mediaType = getCarouselCardFormat(carouselCards[currentCarouselCard]).toLowerCase();
      const cardIndex = currentCarouselCard;
      setVariables((prev) => {
        const cards = [...(prev.carousel?.cards || [])];
        cards[cardIndex] = { media: { type: mediaType, mediaId, id: mediaId, mediaUrl: uploadResult.mediaUrl || "" } };
        return { ...prev, carousel: { cards } };
      });
      setCurrentCarouselCard(null);
      dispatch(clearUploadMediaIDState());
    } else if (uploadStatus === "failed") {
      setCurrentCarouselCard(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadStatus, uploadResult, currentCarouselCard]);

  const handleSelectCarouselFile = (cardIndex, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const format = getCarouselCardFormat(carouselCards[cardIndex]);
    const fileName = file.name.toLowerCase();
    if (format === "DOCUMENT" && !fileName.endsWith(".pdf")) {
      alert(`Please select a .pdf file for Card ${cardIndex + 1}.`);
      if (carouselFileInputRefs.current[cardIndex]) carouselFileInputRefs.current[cardIndex].value = "";
      return;
    }
    if (
      format === "IMAGE" &&
      !(fileName.endsWith(".png") || fileName.endsWith(".jpg") || fileName.endsWith(".jpeg"))
    ) {
      alert(`Please select a .png or .jpg/.jpeg file for Card ${cardIndex + 1}.`);
      if (carouselFileInputRefs.current[cardIndex]) carouselFileInputRefs.current[cardIndex].value = "";
      return;
    }
    if (format === "VIDEO" && !fileName.endsWith(".mp4")) {
      alert(`Please select a .mp4 file for Card ${cardIndex + 1}.`);
      if (carouselFileInputRefs.current[cardIndex]) carouselFileInputRefs.current[cardIndex].value = "";
      return;
    }
    setCurrentCarouselCard(cardIndex);
    dispatch(uploadFileWithMediaID(file));
  };

  const handleClearCarouselFile = (cardIndex) => {
    setVariables((prev) => {
      if (!prev.carousel?.cards?.[cardIndex]) return prev;
      const cards = [...prev.carousel.cards];
      cards[cardIndex] = undefined;
      return { ...prev, carousel: { cards } };
    });
    if (carouselFileInputRefs.current[cardIndex]) carouselFileInputRefs.current[cardIndex].value = "";
  };

  const bodyComp = template?.components?.find((c) => c.type?.toUpperCase() === "BODY");
  const bodyText = bodyComp?.text || "";
  const placeholders = useMemo(() => {
    if (!bodyText) return [];
    const regex = /{{(\d+)}}/g;
    const found = new Set();
    let match;
    while ((match = regex.exec(bodyText)) !== null) {
      found.add(match[1]);
    }
    return [...found].sort((a, b) => Number(a) - Number(b));
  }, [bodyText]);

  const buttonsComp = template?.components?.find((c) => c.type === "BUTTONS");
  const dynamicUrlButtons = useMemo(
    () =>
      (buttonsComp?.buttons || []).filter(
        (b) => (b.type || "").toUpperCase() === "URL" && (b.url || "").includes("{{1}}")
      ),
    [buttonsComp]
  );

  const handleChange = (key, value) => {
    setVariables((prev) => ({ ...prev, [key]: value }));
  };

  const isComplete = useMemo(() => {
    if (isMediaHeader && !variables.headerMedia) return false;
    if (carouselCards.some((_, cardIndex) => !variables.carousel?.cards?.[cardIndex]?.media)) return false;
    if (placeholders.some((index) => !variables[index]?.trim())) return false;
    if (dynamicUrlButtons.some((_, btnIndex) => !variables[`url_${btnIndex}`]?.trim())) return false;
    return true;
  }, [isMediaHeader, carouselCards, placeholders, dynamicUrlButtons, variables]);

  useEffect(() => {
    onValidityChange?.(isComplete);
  }, [isComplete, onValidityChange]);

  const handleSelectFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fileName = file.name.toLowerCase();
    if (headerFormat === "DOCUMENT" && !fileName.endsWith(".pdf")) {
      alert("Please select a .pdf file for DOCUMENT type.");
      fileInputRef.current.value = "";
      return;
    }
    if (
      headerFormat === "IMAGE" &&
      !(fileName.endsWith(".png") || fileName.endsWith(".jpg") || fileName.endsWith(".jpeg"))
    ) {
      alert("Please select a .png or .jpg/.jpeg file for IMAGE type.");
      fileInputRef.current.value = "";
      return;
    }
    if (headerFormat === "VIDEO" && !fileName.endsWith(".mp4")) {
      alert("Please select a .mp4 file for VIDEO type.");
      fileInputRef.current.value = "";
      return;
    }
    dispatch(uploadFileWithMediaID(file));
  };

  const handleClearFile = () => {
    dispatch(clearUploadMediaIDState());
    setVariables((prev) => {
      if (!("headerMedia" in prev)) return prev;
      const { headerMedia: _headerMedia, ...rest } = prev;
      return rest;
    });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (!isMediaHeader && placeholders.length === 0 && dynamicUrlButtons.length === 0 && carouselCards.length === 0) {
    return null;
  }

  const hasHeaderUpload = Boolean(variables.headerMedia);

  return (
    <Box sx={{ mt: 2 }}>
      {isMediaHeader && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="500" gutterBottom>
            Header File ({headerFormat})
          </Typography>
          <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
            {allowedExtensionsLabel}
          </Typography>
          <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
            <input type="file" ref={fileInputRef} style={{ display: "none" }} accept={acceptStr} onChange={handleSelectFile} />
            {uploadStatus === "loading" ? (
              <Button variant="outlined" size="small" disabled sx={{ textTransform: "none" }}>
                <CircularProgress size="1em" sx={{ mr: 1 }} />
                Uploading...
              </Button>
            ) : hasHeaderUpload ? (
              <Button
                variant="outlined"
                size="small"
                color="success"
                startIcon={<CheckIcon />}
                sx={{ textTransform: "none" }}
              >
                Uploaded
              </Button>
            ) : (
              <Button
                variant="outlined"
                size="small"
                startIcon={<CloudUploadIcon />}
                onClick={() => fileInputRef.current?.click()}
                sx={{ textTransform: "none" }}
              >
                Upload File
              </Button>
            )}
            {(hasHeaderUpload || uploadStatus === "failed") && (
              <IconButton size="small" onClick={handleClearFile}>
                <CancelIcon fontSize="small" />
              </IconButton>
            )}
          </Box>
          {uploadError && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
              {uploadError.toString()}
            </Typography>
          )}
        </Box>
      )}

      {carouselCards.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="500" gutterBottom>
            Carousel Media Upload
          </Typography>
          {carouselCards.map((card, cardIndex) => {
            const format = getCarouselCardFormat(card);
            const { acceptStr: cardAccept, label: cardLabel } = getMediaAcceptInfo(format);
            const hasUpload = Boolean(variables.carousel?.cards?.[cardIndex]?.media);
            const isUploading = currentCarouselCard === cardIndex && uploadStatus === "loading";

            return (
              <Box key={`carousel-card-${cardIndex}`} sx={{ mb: 1.5 }}>
                <Typography variant="body2">{`Card ${cardIndex + 1} (${format})`}</Typography>
                <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                  {cardLabel}
                </Typography>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <input
                    type="file"
                    ref={(el) => (carouselFileInputRefs.current[cardIndex] = el)}
                    style={{ display: "none" }}
                    accept={cardAccept}
                    onChange={(e) => handleSelectCarouselFile(cardIndex, e)}
                  />
                  {!hasUpload ? (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<CloudUploadIcon />}
                      onClick={() => carouselFileInputRefs.current[cardIndex]?.click()}
                      disabled={isUploading}
                      sx={{ textTransform: "none" }}
                    >
                      {isUploading ? (
                        <>
                          <CircularProgress size="1em" sx={{ mr: 1 }} />
                          Uploading...
                        </>
                      ) : (
                        `Upload Media for Card ${cardIndex + 1}`
                      )}
                    </Button>
                  ) : (
                    <Button
                      variant="outlined"
                      size="small"
                      color="success"
                      startIcon={<CheckIcon />}
                      sx={{ textTransform: "none" }}
                    >
                      Uploaded
                    </Button>
                  )}
                  {hasUpload && (
                    <IconButton size="small" onClick={() => handleClearCarouselFile(cardIndex)}>
                      <CancelIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            );
          })}
          {uploadError && currentCarouselCard === null && uploadStatus === "failed" && (
            <Typography variant="caption" color="error" display="block" sx={{ mt: 0.5 }}>
              {uploadError.toString()}
            </Typography>
          )}
        </Box>
      )}

      {placeholders.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="500" gutterBottom>
            Fill in the Variables
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {placeholders.map((index) => (
              <VariableValueField
                key={index}
                label={`{{${index}}}`}
                value={variables[index] || ""}
                onChange={(value) => handleChange(index, value)}
                placeholder={`Enter a fixed value or pick a contact field for {{${index}}}`}
              />
            ))}
          </Box>
        </Box>
      )}

      {dynamicUrlButtons.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" fontWeight="500" gutterBottom>
            Dynamic URL Values
          </Typography>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            {dynamicUrlButtons.map((btn, btnIndex) => (
              <VariableValueField
                key={`url-btn-${btnIndex}`}
                label={btn.text || `URL Button ${btnIndex + 1}`}
                value={variables[`url_${btnIndex}`] || ""}
                onChange={(value) => handleChange(`url_${btnIndex}`, value)}
                placeholder="Enter a fixed value or pick a contact field"
                helperText={`Example value for ${btn.text || "URL Button"}`}
              />
            ))}
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default TemplateVariablesForm;
